import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteHeader } from "@/components/site-header";
import { ArrowLeft, Shield, Key, Bell, Loader2, Copy, Check, LogOut, Trash2 } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

type SetupData = {
  slot: string;
  name: string;
  secret: string;
  qrCode: string;
  manualEntry: string;
};

export default function AccountSettingsPage() {
  const { user, isLoading, isAuthenticated, logout, isLoggingOut } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const setupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/2fa/setup/primary", { name: "Primary Authenticator" });
      return res.json();
    },
    onSuccess: (data: SetupData) => {
      setSetupData(data);
      setVerifyCode("");
      setDialogOpen(true);
    },
    onError: () => {
      toast({ title: "Setup failed", description: "Could not start 2FA setup. Please try again.", variant: "destructive" });
    },
  });

  const enableMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await apiRequest("POST", "/api/auth/2fa/enable/primary", { token, name: "Primary Authenticator" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setDialogOpen(false);
      setSetupData(null);
      setVerifyCode("");
      toast({ title: "2FA enabled", description: "Two-factor authentication is now active on your account." });
    },
    onError: () => {
      toast({ title: "Verification failed", description: "The code was incorrect or has expired. Please try again.", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/auth/2fa/primary");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "2FA removed", description: "Two-factor authentication has been disabled." });
    },
    onError: () => {
      toast({ title: "Failed to remove 2FA", description: "Please try again.", variant: "destructive" });
    },
  });

  const notificationsMutation = useMutation({
    mutationFn: async (prefs: { emailNotifications: boolean; marketingEmails: boolean }) => {
      return apiRequest("PATCH", "/api/auth/user/notifications", prefs);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Preferences saved" });
    },
    onError: () => {
      toast({ title: "Failed to save preferences", variant: "destructive" });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("DELETE", "/api/auth/account");
    },
    onSuccess: () => {
      queryClient.clear();
      setLocation("/");
    },
    onError: () => {
      toast({ title: "Failed to delete account", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleCopySecret = () => {
    if (setupData?.manualEntry) {
      navigator.clipboard.writeText(setupData.manualEntry);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (verifyCode.trim()) {
      enableMutation.mutate(verifyCode.trim());
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSetupData(null);
    setVerifyCode("");
  };

  const handleNotificationToggle = (field: "emailNotifications" | "marketingEmails", value: boolean) => {
    if (!user) return;
    notificationsMutation.mutate({
      emailNotifications: field === "emailNotifications" ? value : (user.emailNotifications ?? true),
      marketingEmails: field === "marketingEmails" ? value : (user.marketingEmails ?? false),
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    setLocation("/login");
    return null;
  }

  const confirmEmail = user.email || "";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-6">
          <Link href="/account">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="button-back">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Account
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground">Manage your security and preferences</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Two-Factor Authentication</CardTitle>
                  <CardDescription>Add an extra layer of security to your account</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Status</p>
                  <p className="text-sm text-muted-foreground">
                    {user.isTwoFactorEnabled
                      ? "Your account is protected with 2FA"
                      : "Enable 2FA for additional security"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={user.isTwoFactorEnabled ? "default" : "secondary"} data-testid="status-2fa">
                    {user.isTwoFactorEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                  {user.isTwoFactorEnabled ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeMutation.mutate()}
                      disabled={removeMutation.isPending}
                      data-testid="button-remove-2fa"
                    >
                      {removeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Remove 2FA
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setupMutation.mutate()}
                      disabled={setupMutation.isPending}
                      data-testid="button-setup-2fa"
                    >
                      {setupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Set up 2FA
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Key className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button variant="outline" data-testid="button-change-password">
                Change Password
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Notifications</CardTitle>
                  <CardDescription>Manage how you receive updates</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="email-notifications" className="flex flex-col gap-1">
                  <span>Email Notifications</span>
                  <span className="font-normal text-sm text-muted-foreground">
                    Receive updates about your affiliations and content
                  </span>
                </Label>
                <Switch
                  id="email-notifications"
                  checked={user.emailNotifications ?? true}
                  onCheckedChange={(v) => handleNotificationToggle("emailNotifications", v)}
                  disabled={notificationsMutation.isPending}
                  data-testid="switch-email-notifications"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <Label htmlFor="marketing-emails" className="flex flex-col gap-1">
                  <span>Marketing Emails</span>
                  <span className="font-normal text-sm text-muted-foreground">
                    Receive news and promotional content
                  </span>
                </Label>
                <Switch
                  id="marketing-emails"
                  checked={user.marketingEmails ?? false}
                  onCheckedChange={(v) => handleNotificationToggle("marketingEmails", v)}
                  disabled={notificationsMutation.isPending}
                  data-testid="switch-marketing-emails"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <LogOut className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle>Session</CardTitle>
                  <CardDescription>Manage your active session on this device</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sign Out</p>
                  <p className="text-sm text-muted-foreground">Sign out of your account on this device</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => logout()}
                  disabled={isLoggingOut}
                  data-testid="button-logout"
                >
                  {isLoggingOut && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>Permanent and irreversible account actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Delete Account</p>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete your account and all associated data. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="button-delete-account"
                >
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set up two-factor authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (such as Google Authenticator or Authy), then enter the 6-digit code to confirm.
            </DialogDescription>
          </DialogHeader>

          {setupData && (
            <div className="space-y-5">
              <div className="flex justify-center">
                <img
                  src={setupData.qrCode}
                  alt="2FA QR code"
                  className="w-48 h-48 rounded-lg border"
                  data-testid="img-2fa-qrcode"
                />
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Can't scan? Enter this key manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-md break-all" data-testid="text-2fa-secret">
                    {setupData.manualEntry}
                  </code>
                  <Button variant="outline" size="icon" onClick={handleCopySecret} data-testid="button-copy-secret">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <form onSubmit={handleVerify} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="verify-code">Verification code</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    autoComplete="one-time-code"
                    data-testid="input-2fa-code"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="button-cancel-2fa">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={verifyCode.length !== 6 || enableMutation.isPending}
                    data-testid="button-verify-2fa"
                  >
                    {enableMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verify &amp; Enable
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete your account</DialogTitle>
            <DialogDescription>
              This will permanently delete your account, all your data, and log you out. There is no way to recover it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              To confirm, type your email address: <strong>{confirmEmail}</strong>
            </p>
            <Input
              placeholder={confirmEmail}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              data-testid="input-delete-confirm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(""); }} data-testid="button-cancel-delete">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmText !== confirmEmail || deleteAccountMutation.isPending}
              onClick={() => deleteAccountMutation.mutate()}
              data-testid="button-confirm-delete"
            >
              {deleteAccountMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete my account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
